import React, { useState, useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

export const HowItWorks = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch('/api/create-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, password, turnstileToken }),
      });

      const data = await response.json();

if (response.ok) {
          setSuccess(true);
          setName('');
          setEmail('');
          setPassword('');
          setTurnstileToken('');
          turnstileRef.current?.reset();
      } else {
        setError(data.error || data.message || 'Failed to create account');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="howitworks" className="py-12 sm:py-16 md:py-24 px-2 sm:px-3 md:px-4 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-16">
          <p className="text-[#25D366] font-medium mb-2 sm:mb-3 md:mb-4 uppercase tracking-wide text-[10px] sm:text-xs md:text-sm">How it works</p>
          <h2 className="text-lg sm:text-2xl md:text-4xl lg:text-5xl font-bold text-gray-900 px-1">
            Revolutionizing <span className="italic">Customer Interactions</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="space-y-8 sm:space-y-10 md:space-y-16">
          {/* Step 1 */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-12 items-center">
            <div className="px-1">
              <div className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 bg-blue-50 text-blue-600 px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-medium mb-2 sm:mb-3 md:mb-4">
                <span className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-[8px] sm:text-[10px] md:text-xs">1</span>
                Sign up
              </div>
              <h3 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
                Quick and easy sign up process
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-lg mb-3 sm:mb-4 md:mb-6">
                Get started with waatee in minutes. No complex setup required.
              </p>
              <a
                href="https://app.waatee.com/register"
                className="inline-flex items-center bg-[#25D366] text-white px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full font-medium hover:bg-[#1da851] transition-all text-xs sm:text-sm md:text-base"
              >
                Get started
              </a>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-6">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 md:mb-4">
                    <img src="/waatee-logo.png" alt="waatee" className="h-5 sm:h-6 md:h-8" />
                  </div>
                <h3 className="text-sm sm:text-lg md:text-xl font-bold mb-2 sm:mb-3 md:mb-4">Create Account</h3>
                  {success && (
                    <div className="mb-2 sm:mb-3 md:mb-4 p-2 sm:p-3 bg-green-100 text-green-700 rounded-lg text-[10px] sm:text-xs md:text-sm">
                      Account created successfully! Check your email.
                    </div>
                  )}
                  {error && (
                    <div className="mb-2 sm:mb-3 md:mb-4 p-2 sm:p-3 bg-red-100 text-red-700 rounded-lg text-[10px] sm:text-xs md:text-sm">
                      {error}
                    </div>
                  )}
                    <form onSubmit={handleSubmit} className="space-y-1.5 sm:space-y-2 md:space-y-3 relative z-10">
                      <input 
                        type="text" 
                        placeholder="Full Name" 
                        value={name} 
                        onChange={(e) => setName(e.target.value)} 
                        className="w-full px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent text-gray-900 text-xs sm:text-sm md:text-base" 
                        required 
                      />
                      <input 
                        type="email" 
                        placeholder="Email" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        className="w-full px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent text-gray-900 text-xs sm:text-sm md:text-base" 
                        required 
                      />
                      <input 
                        type="password" 
                        placeholder="Password" 
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)} 
                        className="w-full px-2 sm:px-3 md:px-4 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent text-gray-900 text-xs sm:text-sm md:text-base" 
                        required 
                      />
                      <div className="turnstile-wrapper w-full">
                        <Turnstile
                          ref={turnstileRef}
                          siteKey="0x4AAAAAABgf4XiOY4omf7N_"
                          options={{ size: 'flexible' }}
                          onSuccess={(token) => setTurnstileToken(token)}
                          onError={() => setTurnstileToken('')}
                          onExpire={() => setTurnstileToken('')}
                        />
                      </div>
                      <button
                        type="submit" 
                        disabled={loading || !turnstileToken} 
                        className="w-full bg-[#25D366] text-white py-2 sm:py-2.5 md:py-3 rounded-lg font-medium hover:bg-[#1da851] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm md:text-base"
                      >
                        {loading ? 'Creating...' : 'Sign Up'}
                      </button>
                    </form>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-6">
                <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
                  <h3 className="text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3 md:mb-4">Connect Channels</h3>
                  <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                    <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                        <div className="w-6 sm:w-8 md:w-10 h-6 sm:h-8 md:h-10 bg-blue-500 rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-[10px] sm:text-xs md:text-base">T</span>
                        </div>
                        <span className="font-medium text-xs sm:text-sm md:text-base">Telegram</span>
                      </div>
                      <div className="w-8 sm:w-10 md:w-12 h-4 sm:h-5 md:h-6 bg-[#25D366] rounded-full relative cursor-pointer flex-shrink-0">
                        <div className="absolute right-0.5 top-0.5 w-3 sm:w-3.5 md:w-4 h-3 sm:h-3.5 md:h-4 bg-white rounded-full shadow"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-2 sm:p-3 md:p-4 bg-gray-50 rounded-lg sm:rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
                        <div className="w-6 sm:w-8 md:w-10 h-6 sm:h-8 md:h-10 bg-[#25D366] rounded-md sm:rounded-lg flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-[10px] sm:text-xs md:text-base">W</span>
                        </div>
                        <span className="font-medium text-xs sm:text-sm md:text-base">WhatsApp</span>
                      </div>
                      <div className="w-8 sm:w-10 md:w-12 h-4 sm:h-5 md:h-6 bg-[#25D366] rounded-full relative cursor-pointer flex-shrink-0">
                        <div className="absolute right-0.5 top-0.5 w-3 sm:w-3.5 md:w-4 h-3 sm:h-3.5 md:h-4 bg-white rounded-full shadow"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2 px-1">
              <div className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 bg-pink-50 text-pink-600 px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-medium mb-2 sm:mb-3 md:mb-4">
                <span className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 bg-pink-500 text-white rounded-full flex items-center justify-center text-[8px] sm:text-[10px] md:text-xs">2</span>
                Link
              </div>
              <h3 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
                Connect Telegram & WhatsApp
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-lg">
                Streamline chatbot management by connecting your messaging channels in one click.
              </p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-12 items-center">
            <div className="px-1">
              <div className="inline-flex items-center gap-1 sm:gap-1.5 md:gap-2 bg-yellow-50 text-yellow-600 px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs md:text-sm font-medium mb-2 sm:mb-3 md:mb-4">
                <span className="w-4 sm:w-5 md:w-6 h-4 sm:h-5 md:h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-[8px] sm:text-[10px] md:text-xs">3</span>
                Chat
              </div>
              <h3 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">
                Start revolutionizing interactions
              </h3>
              <p className="text-gray-600 text-xs sm:text-sm md:text-base lg:text-lg">
                Advanced chatbot management capabilities and response management at your fingertips.
              </p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg sm:rounded-xl md:rounded-2xl p-2 sm:p-3 md:p-6">
              <div className="bg-white rounded-lg sm:rounded-xl shadow-lg p-3 sm:p-4 md:p-6">
                <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 mb-2 sm:mb-3 md:mb-4">
                  <div className="w-5 sm:w-6 md:w-8 h-5 sm:h-6 md:h-8 bg-[#25D366] rounded-full flex-shrink-0"></div>
                  <span className="font-semibold text-xs sm:text-sm md:text-base">Chatbot</span>
                  <span className="ml-auto text-[8px] sm:text-[10px] md:text-xs bg-green-100 text-green-600 px-1 sm:px-1.5 md:px-2 py-0.5 rounded-full">Online</span>
                </div>
                <div className="space-y-1.5 sm:space-y-2 md:space-y-3">
                  <div className="bg-gray-100 rounded-lg sm:rounded-xl rounded-tl-none p-1.5 sm:p-2 md:p-3 max-w-[90%]">
                    <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm">Hello! How can I help you today?</p>
                  </div>
                  <div className="bg-[#25D366] rounded-lg sm:rounded-xl rounded-tr-none p-1.5 sm:p-2 md:p-3 max-w-[90%] ml-auto">
                    <p className="text-white text-[10px] sm:text-xs md:text-sm">I need help with my order</p>
                  </div>
                  <div className="bg-gray-100 rounded-lg sm:rounded-xl rounded-tl-none p-1.5 sm:p-2 md:p-3 max-w-[90%]">
                    <p className="text-gray-700 text-[10px] sm:text-xs md:text-sm">Sure! Please provide your order ID.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
